export type Frequency = 'hourly' | 'daily' | 'weekdays' | 'weekly' | 'once'

export interface IAgentSchedule {
  id: string
  agent_id: string
  cron: string
  message: string
  description: string | null
  job_id: string | null
  is_one_time: boolean
  tz_name: string | null
  created_at: string
}

const DAY_ABBR: Record<number, string> = { 0: 'Sun', 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat' }

/* ── cron helpers ── */
export function buildCronExpression(
  frequency: Frequency,
  time: string,
  daysOfWeek: number[],
  hourlyMinute: number,
  onceDate: Date | null
): string {
  const [hourStr, minuteStr] = time.split(':')
  const hour = parseInt(hourStr, 10)
  const minute = parseInt(minuteStr, 10)

  switch (frequency) {
    case 'hourly':
      return `${hourlyMinute} * * * *`
    case 'daily':
      return `${minute} ${hour} * * *`
    case 'weekdays':
      return `${minute} ${hour} * * 1-5`
    case 'weekly': {
      const days = [...daysOfWeek].sort((a, b) => a - b)
      const dowField = days.length > 0 ? days.join(',') : String(new Date().getDay())
      return `${minute} ${hour} * * ${dowField}`
    }
    case 'once': {
      const d = onceDate || new Date()
      return `${minute} ${hour} ${d.getDate()} ${d.getMonth() + 1} *`
    }
    default:
      return '0 9 * * *'
  }
}

export function buildScheduleDescription(
  frequency: Frequency,
  time: string,
  daysOfWeek: number[],
  hourlyMinute: number,
  onceDate: Date | null
): string {
  switch (frequency) {
    case 'hourly':
      return `Every hour at minute ${hourlyMinute}`
    case 'daily':
      return `Every day at ${time}`
    case 'weekdays':
      return `Weekdays at ${time}`
    case 'weekly': {
      const names = [...daysOfWeek]
        .sort((a, b) => a - b)
        .map((d) => DAY_ABBR[d])
        .join(', ')
      return `Weekly on ${names} at ${time}`
    }
    case 'once': {
      const d = onceDate || new Date()
      // DD/MM/YYYY to match CustomDatePicker's dateFormat="dd/MM/yyyy" below —
      // toLocaleDateString() would render M/D/YYYY in en-US, showing a different
      // digit order for the same date right next to the picker that picked it.
      const dd = String(d.getDate()).padStart(2, '0')
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      return `Once on ${dd}/${mm}/${d.getFullYear()} at ${time}`
    }
    default:
      return time
  }
}

interface IScheduleForm {
  frequency: Frequency
  time: string
  daysOfWeek: number[]
  hourlyMinute: number
  onceDate: Date | null
}

// Best-effort reverse mapping from a stored cron string back into form
// state, for opening the Edit form. Cron shapes outside the presets this
// UI can produce (e.g. a chat-tool-created "*/15 * * * *") get coerced to
// the closest preset — unavoidable given no raw cron is ever shown here.
export function parseCronToFormState(cron: string, isOneTime: boolean): IScheduleForm {
  const todayIndex = new Date().getDay()
  const fallback: IScheduleForm = {
    frequency: 'daily',
    time: '09:00',
    daysOfWeek: [todayIndex],
    hourlyMinute: 0,
    onceDate: null,
  }

  const parts = cron.trim().split(/\s+/)
  if (parts.length !== 5) return fallback
  const [minute, hour, dom, month, dow] = parts

  const minuteNum = parseInt(minute, 10)
  const safeMinute = Number.isFinite(minuteNum) ? minuteNum : 0

  if (isOneTime) {
    const hourNum = parseInt(hour, 10)
    const domNum = parseInt(dom, 10)
    const monthNum = parseInt(month, 10)
    if (!Number.isFinite(hourNum) || !Number.isFinite(domNum) || !Number.isFinite(monthNum)) return fallback
    // Standard cron has no year field. A one-time schedule can never be in the past
    // (enforced by the picker's minDate at creation), so if this month/day already
    // passed this year, it must actually be next year — same assumption APScheduler's
    // own CronTrigger makes when computing the real next-fire time.
    const now = new Date()
    const todayAtMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    let year = now.getFullYear()
    if (new Date(year, monthNum - 1, domNum) < todayAtMidnight) {
      year += 1
    }
    const onceDate = new Date(year, monthNum - 1, domNum)
    const time = `${String(hourNum).padStart(2, '0')}:${String(safeMinute).padStart(2, '0')}`
    return { frequency: 'once', time, daysOfWeek: [todayIndex], hourlyMinute: 0, onceDate }
  }

  if (hour === '*') {
    return { ...fallback, frequency: 'hourly', hourlyMinute: safeMinute }
  }

  const hourNum = parseInt(hour, 10)
  const safeHour = Number.isFinite(hourNum) ? hourNum : 9
  const time = `${String(safeHour).padStart(2, '0')}:${String(safeMinute).padStart(2, '0')}`

  if (dow === '*') {
    return { ...fallback, frequency: 'daily', time }
  }
  if (dow === '1-5') {
    return { ...fallback, frequency: 'weekdays', time, daysOfWeek: [1, 2, 3, 4, 5] }
  }
  const days = dow
    .split(',')
    .map((d) => parseInt(d, 10))
    .filter((d) => Number.isFinite(d) && d >= 0 && d <= 6)
  return { ...fallback, frequency: 'weekly', time, daysOfWeek: days.length > 0 ? days : [todayIndex] }
}

export const formatOffset = (offset: number) =>
  `UTC${offset < 0 ? '−' : '+'}${String(Math.floor(Math.abs(offset))).padStart(2, '0')}:${String(
    Math.round((Math.abs(offset) % 1) * 60)
  ).padStart(2, '0')}`
